import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { mediaNetworkApi } from '@/api/media-network/mediaNetworkApi';
import { mediaNetworkKeys } from '@/api/media-network/mediaNetworkQueries';
import { MediaType, renditionForProfile, type CreativeRendition, type DisplayProfile } from '@/models/media-network/Creative';
import type { Playlist, PlaylistItem } from '@/models/media-network/Playlist';
import { DEFAULT_SLIDESHOW_INTERVAL_SECONDS } from '@/models/media-network/PlaylistConfiguration';
import { useAppContext } from '../../../AppContext';
import { useLocalizedStrings } from '../../../localization/LocalizedStringsProvider';

interface Props {
    open: boolean;
    playlist: Playlist;
    brandId: string;
    onClose: () => void;
}

// Re-mint a read URI this far before it dies. The SAS lives 15 minutes and a preview left open loops well
// past that, so a URL fetched once would start 403ing mid-rotation.
const URI_REFRESH_MARGIN_MS = 60_000;

interface Slide {
    item: PlaylistItem;
    rendition: CreativeRendition;
}

// What this playlist looks like on a device.
//
// The point is NOT the slideshow — it is the SHAPE. An item with no rendition at the selected shape is
// silently skipped on that screen, and until now the only hint was a warning at the moment it was added.
// So the preview plays only what that shape would actually play, and names what it dropped.
//
// Deliberately previews the ROTATION, not the schedule: it does not know about day parts, or which playlist
// wins at a given location. That is the resolve timeline, a different feature.
const PlaylistPreview = ({ open, playlist, brandId, onClose }: Props) => {
    const strings = useLocalizedStrings();
    const app = useAppContext();

    const [profileId, setProfileId] = useState('');
    const [index, setIndex] = useState(0);
    const [playing, setPlaying] = useState(true);
    const [failed, setFailed] = useState(false);

    const videoRef = useRef<HTMLVideoElement>(null);

    // Shapes for the apps THIS playlist plays on, not the whole catalogue — offering 4:5 for a Tabletop-only
    // playlist would invite worrying about a screen it will never reach.
    const { data: appProfiles = [], isPending: profilesPending } = useQuery({
        queryKey: mediaNetworkKeys.brandDisplayProfiles(brandId),
        enabled: open,
        queryFn: () => mediaNetworkApi.listBrandDisplayProfiles(brandId),
    });

    const profiles = useMemo(() => {
        const byId = new Map<string, DisplayProfile>();
        for (const entry of appProfiles) {
            if (!playlist.apps.includes(entry.appType)) continue;
            for (const p of entry.profiles) byId.set(p.id, p);
        }
        return [...byId.values()];
    }, [appProfiles, playlist.apps]);

    const profile = profiles.find((p) => p.id === profileId) ?? profiles[0];

    // Only meaningful with a location selected. Without one the built-in default is the honest answer — the
    // caption below says which number is in play either way.
    const { data: effective } = useQuery({
        queryKey: mediaNetworkKeys.effectiveConfiguration(app.locationId ?? ''),
        enabled: open && !!app.locationId,
        queryFn: () => mediaNetworkApi.getEffectiveConfiguration(app.locationId!),
    });

    const intervalSeconds = playlist.slideshowIntervalSeconds
        ?? effective?.slideshowIntervalSeconds
        ?? DEFAULT_SLIDESHOW_INTERVAL_SECONDS;

    // The rotation as this shape would see it. An item with no rendition here is not an error — the device
    // just moves past it.
    const slides = useMemo<Slide[]>(() => {
        if (!profile) return [];
        return [...playlist.items]
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((item) => ({ item, rendition: renditionForProfile(item.creative, profile) }))
            .filter((s): s is Slide => !!s.rendition);
    }, [playlist.items, profile]);

    const skipped = useMemo(() => {
        if (!profile) return [];
        return playlist.items
            .filter((i) => !renditionForProfile(i.creative, profile))
            .map((i) => i.creative.name);
    }, [playlist.items, profile]);

    // renditionId -> a URI and when it stops working.
    const uris = useRef(new Map<string, { url: string; expires: number }>());
    const [, bump] = useReducer((n: number) => n + 1, 0);

    const ensureUri = useCallback(async (creativeId: string, renditionId: string) => {
        const hit = uris.current.get(renditionId);
        if (hit && hit.expires - Date.now() > URI_REFRESH_MARGIN_MS) return;
        try {
            const res = await mediaNetworkApi.getRenditionReadUri(creativeId, renditionId);
            uris.current.set(renditionId, { url: res.readUri, expires: new Date(res.expiresOn).getTime() });
            bump();
        } catch {
            // Left uncached so the next pass round the loop tries again.
        }
    }, []);

    const current = slides.length ? slides[index % slides.length] : undefined;
    const currentUrl = current ? uris.current.get(current.rendition.id)?.url : undefined;

    // Fetch the current slide and the one after it. Prefetching the next is what keeps the gap between slides
    // from going black — the device never blanks, and a preview that does would misrepresent it.
    useEffect(() => {
        if (!open || !slides.length) return;
        const at = index % slides.length;
        const next = slides[(at + 1) % slides.length];
        void ensureUri(slides[at].item.creative.id, slides[at].rendition.id);
        void ensureUri(next.item.creative.id, next.rendition.id);
    }, [open, index, slides, ensureUri]);

    const advance = useCallback((delta: number) => {
        setFailed(false);
        setIndex((i) => (slides.length ? (i + delta + slides.length) % slides.length : 0));
    }, [slides.length]);

    // Images advance on a timer, videos on end-of-media — which is what the device does, rather than trusting
    // the stored duration.
    useEffect(() => {
        if (!open || !playing || !currentUrl || !current) return;
        if (current.rendition.mediaType === MediaType.Video) return;

        const t = setTimeout(() => advance(1), intervalSeconds * 1000);
        return () => clearTimeout(t);
    }, [open, playing, currentUrl, current, intervalSeconds, advance]);

    useEffect(() => {
        if (playing) void videoRef.current?.play().catch(() => undefined);
        else videoRef.current?.pause();
    }, [playing, currentUrl]);

    // A fresh run each time it opens, and whenever the shape changes — the rotation is a different list.
    useEffect(() => {
        setIndex(0);
        setFailed(false);
        setPlaying(open);
    }, [open, profileId]);

    const shapeLabel = profile ? `${profile.name} (${profile.display})` : '';

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{strings.playlistPreviewTitle.replace('{{name}}', playlist.name)}</DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <FormControl sx={{ minWidth: 240 }} size="small">
                            <InputLabel id="preview-shape-label">{strings.playlistPreviewShape}</InputLabel>
                            <Select
                                labelId="preview-shape-label"
                                label={strings.playlistPreviewShape}
                                value={profile?.id ?? ''}
                                onChange={(e) => setProfileId(e.target.value)}
                                disabled={profiles.length < 2}
                            >
                                {profiles.map((p) => (
                                    <MenuItem key={p.id} value={p.id}>{p.name} ({p.display})</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Typography variant="caption" color="text.secondary">
                            {strings.playlistPreviewInterval.replace('{{seconds}}', String(intervalSeconds))}
                        </Typography>
                    </Stack>

                    {!profilesPending && profiles.length === 0 && (
                        <Alert severity="warning" variant="outlined">{strings.playlistPreviewNoShapes}</Alert>
                    )}

                    {/* Every item missing this shape means the screen shows nothing at all — the one case the
                        never-blank rule cannot rescue, because there is nothing to fall back to. */}
                    {profile && slides.length === 0 && playlist.items.length > 0 && (
                        <Alert severity="error" variant="outlined">
                            {strings.playlistPreviewNothing.replace('{{shape}}', shapeLabel)}
                        </Alert>
                    )}

                    {skipped.length > 0 && slides.length > 0 && (
                        <Alert severity="warning" variant="outlined">
                            {strings.playlistPreviewSkipped
                                .replace('{{count}}', String(skipped.length))
                                .replace('{{names}}', skipped.join(', '))}
                        </Alert>
                    )}

                    {/* Black, at the shape's exact ratio, with the media letterboxed inside it — the same
                        thing the device does, so a mismatched file looks wrong HERE rather than on a screen
                        in a restaurant. */}
                    {current && (
                        <Box
                            sx={{
                                height: '55vh',
                                aspectRatio: `${profile!.widthRatio} / ${profile!.heightRatio}`,
                                mx: 'auto',
                                bgcolor: 'common.black',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                            }}
                        >
                            {failed ? (
                                <Typography variant="body2" sx={{ color: 'common.white', p: 2, textAlign: 'center' }}>
                                    {strings.playlistPreviewError}
                                </Typography>
                            ) : !currentUrl ? (
                                <CircularProgress size={28} sx={{ color: 'common.white' }} />
                            ) : current.rendition.mediaType === MediaType.Video ? (
                                <video
                                    ref={videoRef}
                                    key={current.rendition.id}
                                    src={currentUrl}
                                    autoPlay
                                    muted
                                    playsInline
                                    onEnded={() => advance(1)}
                                    onError={() => setFailed(true)}
                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                />
                            ) : (
                                <img
                                    key={current.rendition.id}
                                    src={currentUrl}
                                    alt={current.item.creative.name}
                                    onError={() => setFailed(true)}
                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                />
                            )}
                        </Box>
                    )}

                    {current && (
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                            <IconButton onClick={() => advance(-1)} aria-label="previous item">
                                <ChevronLeftIcon />
                            </IconButton>
                            <IconButton onClick={() => setPlaying((p) => !p)} aria-label={playing ? 'pause' : 'play'}>
                                {playing ? <PauseIcon /> : <PlayArrowIcon />}
                            </IconButton>
                            <IconButton onClick={() => advance(1)} aria-label="next item">
                                <ChevronRightIcon />
                            </IconButton>

                            <Stack sx={{ ml: 2, minWidth: 0 }}>
                                <Typography variant="body2" noWrap>{current.item.creative.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {strings.playlistPreviewPosition
                                        .replace('{{index}}', String((index % slides.length) + 1))
                                        .replace('{{count}}', String(slides.length))}
                                </Typography>
                            </Stack>
                        </Stack>
                    )}

                    {/* Said plainly rather than left to be discovered: someone WILL open this to work out why
                        the wrong thing is on a screen, and this is not the tool for that question. */}
                    <Typography variant="caption" color="text.secondary">
                        {strings.playlistPreviewRotationOnly}
                    </Typography>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button variant="outlined" onClick={onClose}>{strings.close}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default PlaylistPreview;
